import React, { useContext, useEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";

import { Col, Container, Row } from "react-bootstrap";

import { useTranslation } from "react-i18next";
import { ng } from "@common/ngds";

import {
  SXEditField,
  removeInvalidTextFromNumberString,
  UnitCreator,
} from "@common/components/ui-components/SXEditField";

import SectionSVG from "../SectionSVG";

import { Dropdown } from "react-bootstrap";
import { getSymbolLabelAsString } from "../../helpers/auxiliary";
import { AlertMessage } from "../ui-components/SXAlert/SXAlertMessage";

interface NGCustomSectionModalProps {
  close: (reason: number, sec: ng.sections.Section) => void;
  show?: boolean;
  sec: ng.sections.Section;
  setSec: (sec: ng.sections.Section) => void;
  type: string;
  state: string;
  CreateSectionInDB: (sec: ng.sections.Section) => Promise<ng.sections.Section>;
  UpdateSectionInDB: (sec: ng.sections.Section) => Promise<ng.sections.Section>;
  IsNameTaken: (name: string, type: ng.sections.SectionType, guid?: string) => boolean;
}

interface NGCustomSectionProps {
  workingSec: ng.sections.Section;
  setSec: (sec: ng.sections.Section) => void;
  type: string;
}

export const NGCustomSectionModal = (props: NGCustomSectionModalProps) => {
  const { t } = useTranslation();

  const type = props.type;

  const footer = (
    <>
      <Button
        id="reject_section"
        className="modal-btn"
        onClick={(e) => {
          props.close(-1, props.sec);
        }}
      >
        {t("button_cancel_.label")}
      </Button>
      <Button
        id="accept_section"
        className="modal-btn"
        onClick={async (e) => {
          if (props.sec && props.sec.id) {
            if (props.state === "create") {
              // For create, the name must be unique
              if (props.IsNameTaken(props.sec.id.name, props.sec.type, "")) {
                AlertMessage(
                  "error_",
                  t("create_.label"),
                  "",
                  t("MSG_section_name_is_taken_.label") + ` (${props.sec.id.name})`,
                );
                return;
              }

              const newSec = await props.CreateSectionInDB(props.sec);

              props.close(1, newSec);
            } else {
              // For update, the name must be unique, except for the current section (hence the guid as input)
              if (props.IsNameTaken(props.sec.id.name, props.sec.type, props.sec.id.guid)) {
                AlertMessage(
                  "error_",
                  t("update_.label"),
                  "",
                  t("MSG_section_name_is_taken_.label") + ` (${props.sec.id.name})`,
                );
                return;
              }

              const updatedSec = await props.UpdateSectionInDB(props.sec);

              props.close(1, updatedSec);
            }
          }
        }}
      >
        {t("button_save_.label")}
      </Button>
    </>
  );
  const body = (
    <>
      <NGCustomSection workingSec={props.sec.deepClone()} type={type} setSec={props.setSec} />
    </>
  );
  const header = (type: string) => (
    <>
      {t("custom_.label")}
      &nbsp;
      {type}-{t("section_.label")}
    </>
  );

  return <></>;
};

// O P1 P2:  P1 = P2= ## O= 1=normal, 2=horisontal, 3=vertival  eg. 20102 = hosizontal line from p1 to p2
const schema = {
  R: [30105, 20307], // height, width
  PL: [30105, 0], // height, width
  T: [30109, 30509, 20711, 20315], // height, flangeHeight, flangeWidth, webWidth
  VT: [30109, 30509, 20711, 20414, 20002], // height, flangeHeight, flangeWidth, topWebWidth, bottomWebWidth
  VR: [30105, 20406, 20002], // height, topWidth, bottomWidth
  F: [30109, 30501, 20315, 20711], // height, flangeHeight, flangeWidth, webWidth
  L_2: [30901, 30501, 20300, 20710, 0], // height, flangeHeight, flangeWidth, webWidth, side
  L_1: [30501, 30900, 21102, 20704, 0], // height, flangeHeight, flangeWidth, webWidth, side --- for left side
  KB: [30901, 31300, 30502, 11400, 10204, 20002, 20406, 21214], // height, leftFlangeHeight, rightFlangeHeight, inclinationLeftFlange, inclinationRightFlange, bottomWidth, leftSupportWidth, rightSupportWidth
  KBE_2: [30901, 30502, 20710, 20002, 20406, 0], // height, flangeHeight, webWidth, bottomWidth, supportWidth, (side)
  KBE_1: [30502, 30900, 20704, 20002, 20810, 0], // height, flangeHeight, webWidth, bottomWidth, supportWidth, (side) --- for left side
  I: [31301, 31012, 30810, 30604, 30204, 21115, 20719, 20300], // height, topFlangeHeight, topWebHeight, bottomWebHeight, bottomFlangeHeight, topFlangeWidth, webWidth, bottomFlangeWidth

  HSQ: [0, 31401, 11315, 10002, 11014, 10105, 12127, 13104], //
  HSQ_1: [0, 31401, 11315, 10002, 11014, 10105, 12125, 12904, 20626], //
  HSQ_2: [0, 31401, 11315, 10002, 11014, 10105, 13236, 20618, 13104], //
  HSQ_3: [0, 31401, 11315, 10002, 11014, 10105, 12125, 0, 0], //

  UX: [30122, 20002, 21216, 30105], //
  ZX: [32200, 20002, 21216, 30105], //

  Sawn: [30105, 20307], // height, width
  Dressed: [30105, 20307], // height, width
  Glulam: [30105, 20307], // height, width
  Kerto: [30105, 20307], // height, width
};
export const NGCustomSection = (props: NGCustomSectionProps) => {
  const userLang = "en";
  let settings = {};
  let type: string = props.type;
  const circleSegmentCount = 48;

  // Define types
  type RParams = ng.sections.RParams;
  type keyofRParams = ng.sections.RParams_keys;
  type PLParams = ng.sections.RParams;
  type keyofPLParams = ng.sections.RParams_keys;
  type TParams = ng.sections.TParams;
  type keyofTParams = ng.sections.TParams_keys;
  type VTParams = ng.sections.VTParams;
  type keyofVTParams = ng.sections.VTParams_keys;
  type VRParams = ng.sections.VRParams;
  type keyofVRParams = ng.sections.VRParams_keys;
  type FParams = ng.sections.FParams;
  type keyofFParams = ng.sections.FParams_keys;
  type LParams = ng.sections.LParams;
  type keyofLParams = ng.sections.LParams_keys;
  type KBParams = ng.sections.KBParams;
  type keyofKBParams = ng.sections.KBParams_keys;
  type KBEParams = ng.sections.KBEParams;
  type keyofKBEParams = ng.sections.KBEParams_keys;
  type IParams = ng.sections.IParams;
  type keyofIParams = ng.sections.IParams_keys;
  type HSQParams = ng.sections.HSQParams;
  type keyofHSQParams = ng.sections.HSQParams_keys;
  type UXParams = ng.sections.UXParams;
  type keyofUXParams = ng.sections.UXParams_keys;
  type ZXParams = ng.sections.ZXParams;
  type keyofZXParams = ng.sections.ZXParams_keys;
  type CParams = ng.sections.CParams;
  type keyofCParams = ng.sections.CParams_keys;
  type SParams = ng.sections.SParams;
  type keyofSParams = ng.sections.SParams_keys;

  // Now, we define a union type for `dim` based on possible values
  type DimType =
    | RParams
    | PLParams
    | TParams
    | VTParams
    | VRParams
    | FParams
    | LParams
    | KBParams
    | KBEParams
    | IParams
    | HSQParams
    | UXParams
    | ZXParams
    | CParams
    | SParams
    | null;

  const [tmpDim, setTmpDim] = React.useState<DimType>(null);
  const [hlpNbr, setHlpNbr] = React.useState<number>(0);

  let radioBtn = 0; //NOTE! this must always be set to 0 when not in use otherwise the helping lines will not work

  const GetDim = React.useCallback(
    (sec: ng.sections.Section) => {
      let dim: DimType = null;

      switch (type) {
        //============ CONCRETE ===============
        case "R":
          dim = sec.getRParams();
          if (!dim) {
            //this is for Rectangular timber-sections which lacks rParams in db
            let params = ng.sections.RParams.create({
              height: sec.units!.cog!.y * 2,
              width: sec.units!.cog!.x * 2,
            });

            sec.setRParams(params);
            props.setSec(sec);
          }
          dim = sec.getRParams();
          break;
        case "C":
          dim = sec.getCParams();
          break;
        case "S":
          {
            dim = sec.getSParams();
            let params = ng.sections.SParams.create({
              side: dim?.side,
            });
            sec.setSParams(params);
          }
          break;
        case "PL":
          dim = sec.getRParams();
          break;
        case "T":
          dim = sec.getTParams();
          break;
        case "VT":
          dim = sec.getVtParams();
          break;
        case "VR":
          dim = sec.getVrParams();
          break;
        case "F":
          dim = sec.getFParams();
          break;
        case "L":
          dim = sec.getLParams();
          break;
        case "KB":
          dim = sec.getKbParams();
          break;
        case "KBE":
          dim = sec.getKbeParams();
          break;
        case "I":
          dim = sec.getIParams();
          break;
        //============ STEEL ===============
        case "HSQ":
          dim = sec.getHsqParams();
          break;
        case "UX":
          dim = sec.getUxParams();
          break;
        case "ZX":
          dim = sec.getZxParams();
          break;

        //============ TIMBER ===============
        case "Sawn":
        case "Dressed":
        case "Glulam":
        case "Kerto":
          dim = sec.getRParams();
          if (!dim) {
            let params = ng.sections.RParams.create({
              height: sec.units!.cog!.y * 2,
              width: sec.units!.cog!.x * 2,
            });

            sec.setRParams(params);
            props.setSec(sec);
          }
          dim = sec.getRParams();
          break;

        default:
          dim = null;
          break;
      }
      return dim;
    },
    [props, type],
  );

  const updatedDim = useRef(false);
  useEffect(() => {
    if (updatedDim.current) return;
    if (props.workingSec) {
      setTmpDim(GetDim(props.workingSec));
    }
    updatedDim.current = false;
  }, [GetDim, props.workingSec]);

  //================================================================================================

  function MouseEnter(e: React.MouseEvent) {
    let elem = e.currentTarget as HTMLElement;

    let id: number = Number(elem.id);

    let secType = type;

    if (radioBtn > 0) {
      secType += "_" + radioBtn;
    }

    let arr: number[] = schema[secType as keyof typeof schema];

    if (arr !== undefined && id <= arr.length) {
      setHlpNbr(arr[id - 1]);
    }
  }

  function MouseLeave() {
    setHlpNbr(0);
  }

  function GetUnitVal(dim: DimType, k: string) {
    let unitVal: number | string = ""; // Fixing default value

    switch (type) {
      //============ CONCRETE ===============
      case "R":
        const rParams = dim as RParams; // Assert that dim is of type RParams
        unitVal = rParams[k as keyofRParams] || "";
        break;
      case "S":
        const sParams = dim as SParams; // Assert that dim is of type SParams
        unitVal = sParams[k as keyofSParams] || "";
        break;
      case "C":
        const cParams = dim as CParams; // Assert that dim is of type CParams
        unitVal = cParams[k as keyofCParams] || "";
        break;
      case "PL":
        const plParams = dim as PLParams; // Assert that dim is of type RParams
        unitVal = plParams[k as keyofPLParams] || "";
        break;
      case "T":
        const tParams = dim as TParams; // Assert that dim is of type TParams
        unitVal = tParams[k as keyofTParams] || "";
        break;
      case "VT":
        const vtParams = dim as VTParams; // Assert that dim is of type VTParams
        unitVal = vtParams[k as keyofVTParams] || "";
        break;
      case "VR":
        const vrParams = dim as VRParams; // Assert that dim is of type VRParams
        unitVal = vrParams[k as keyofVRParams] || "";
        break;
      case "F":
        const fParams = dim as FParams; // Assert that dim is of type FParams
        unitVal = fParams[k as keyofFParams] || "";
        break;
      case "L":
        const lParams = dim as LParams; // Assert that dim is of type LParams
        unitVal = lParams[k as keyofLParams] || "";
        break;
      case "KB":
        const kbParams = dim as KBParams; // Assert that dim is of type KBParams
        unitVal = kbParams[k as keyofKBParams] || "";
        break;
      case "KBE":
        const kbeParams = dim as KBEParams; // Assert that dim is of type KBEParams
        unitVal = kbeParams[k as keyofKBEParams] || "";
        break;
      case "I":
        const iParams = dim as IParams; // Assert that dim is of type IParams
        unitVal = iParams[k as keyofIParams] || "";
        break;
      //============ STEEL ===============
      case "HSQ":
        const hsqParams = dim as HSQParams; // Assert that dim is of type HSQParams
        unitVal = hsqParams[k as keyofHSQParams] || "";
        if (k === "cutOff" && unitVal === "") {
          unitVal = 0;
        }
        break;
      case "UX":
        const uxParams = dim as UXParams; // Assert that dim is of type HSQParams
        unitVal = uxParams[k as keyofUXParams] || "";
        break;
      case "ZX":
        const zxParams = dim as ZXParams; // Assert that dim is of type HSQParams
        unitVal = zxParams[k as keyofZXParams] || "";
        break;
      //============ TIMBER ===============
      case "Sawn":
      case "Dressed":
      case "Glulam":
      case "Kerto":
        const rxParams = dim as RParams; // Assert that dim is of type RParams
        unitVal = rxParams[k as keyofRParams] || "";
        break;

      default:
        break;
    }

    return unitVal;
  }

  function funUpdate(value: number | string, k: string) {
    if (k === "changeLabel") {
      value = value as string;
    } else {
      value = Number(value);
    }

    let perim = [];
    // let hole = [];
    let add = [];
    let adds = [];

    let ok = true;

    let msg = "";
    let title = t("section_.label");
    let subtitle = t("custom_.label");

    let minVal = 0; // Minimum value
    let maxVal = 0; // Maximum value
    if (props.workingSec!.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_CONCRETE) {
      minVal = 50; // Minimum value
      maxVal = 2500; // Maximum value
      if (
        k !== "side" &&
        k !== "inclinationLeftFlange" &&
        k !== "inclinationRightFlange" &&
        k !== "bottomWebHeight" &&
        k !== "topWebHeight" &&
        k !== "diameter" &&
        Number(value) < minVal
      ) {
        ok = false;
        msg = t("MSG_value_must_be_greater_than_.label") + " " + minVal + " mm";
      }
      if (k !== "side" && k !== "inclinationLeftFlange" && k !== "inclinationRightFlange" && Number(value) > maxVal) {
        ok = false;
        msg = t("MSG_value_must_be_less_than_.label") + " " + maxVal + " mm";
      }
    }
    if (props.workingSec!.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_STEEL) {
      minVal = 3; // Minimum value
      maxVal = 75; // Maximum value
      if (k !== "indent" && k !== "indentCut" && k !== "cutOff" && Number(value) < minVal) {
        ok = false;
        msg = t("MSG_value_must_be_greater_than_.label") + " " + minVal + " mm";
      }
      if (
        (k === "topFlangeThickness" ||
          k === "webThickness" ||
          k === "bottomFlangeThickness" ||
          k === "flangeThickness") &&
        Number(value) > maxVal
      ) {
        ok = false;
        msg = t("MSG_value_must_be_less_than_.label") + " " + maxVal + " mm";
      }
    }
    if (props.workingSec!.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_TIMBER) {
      minVal = 20; // Minimum value
      maxVal = 800; // Maximum value
      if (Number(value) < minVal) {
        ok = false;
        msg = t("MSG_value_must_be_greater_than_.label") + " " + minVal + " mm";
      }
      if (Number(value) > maxVal) {
        ok = false;
        msg = t("MSG_value_must_be_less_than_.label") + " " + maxVal + " mm";
      }
    }

    if (ok && props.workingSec) {
      if (k === "changeLabel") {
        // Update section name
        if (props.workingSec.id) {
          props.workingSec.id.name = value as string;
        }
      } else {
        switch (type) {
          //============ CONCRETE ===============
          case "R":
            if (props.workingSec.params?.type === "rParams") {
              let h = props.workingSec.params.rParams.height;
              let w = props.workingSec.params.rParams.width;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.rParams.height = h;
              }
              if (k === "width") {
                w = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.rParams.width = w;
              }

              perim.push([-w / 2, -h / 2]);
              perim.push([w / 2, -h / 2]);
              perim.push([w / 2, h / 2]);
              perim.push([-w / 2, h / 2]);
              perim.push([-w / 2, -h / 2]);
            }
            break;
          case "S":
            if (props.workingSec.params?.type === "sParams") {
              if (k === "side") {
                let sideValue = Number(value) * 1e-3;
                props.workingSec.params.sParams.side = sideValue;

                perim.push([-sideValue / 2, -sideValue / 2]);
                perim.push([sideValue / 2, -sideValue / 2]);
                perim.push([sideValue / 2, sideValue / 2]);
                perim.push([-sideValue / 2, sideValue / 2]);
              }
            }
            break;

          case "PL":
            if (props.workingSec.params?.type === "rParams") {
              let h = props.workingSec.params.rParams.height;
              let w = props.workingSec.params.rParams.width;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.rParams.height = h;
              }

              perim.push([-w / 2, -h / 2]);
              perim.push([w / 2, -h / 2]);
              perim.push([w / 2, h / 2]);
              perim.push([-w / 2, h / 2]);
              perim.push([-w / 2, -h / 2]);
            }
            break;
          case "C":
            if (props.workingSec.params?.type === "cParams") {
              let diameter = props.workingSec.params.cParams.diameter;

              if (k === "diameter") {
                diameter = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.cParams.diameter = diameter;
              }

              const radius = diameter / 2;
              for (let index = 0; index <= circleSegmentCount; index++) {
                const angle = (index / circleSegmentCount) * Math.PI * 2;
                perim.push([radius * Math.cos(angle), radius * Math.sin(angle)]);
              }
            }
            break;
          case "T":
            if (props.workingSec.params?.type === "tParams") {
              let h = props.workingSec.params.tParams.height;
              let hfl = props.workingSec.params.tParams.flangeHeight;
              let wfl = props.workingSec.params.tParams.flangeWidth;
              let ww = props.workingSec.params.tParams.webWidth;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.tParams.height = h;
              }
              if (k === "flangeHeight") {
                hfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.tParams.flangeHeight = hfl;
              }
              if (k === "flangeWidth") {
                wfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.tParams.flangeWidth = wfl;
              }
              if (k === "webWidth") {
                ww = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.tParams.webWidth = ww;
              }

              if (Math.round(hfl * 1e3) > Math.round(h * 1e3) / 2 || Math.round((wfl - ww) * 1e3) < 2 * minVal) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              perim.push([-ww / 2, -h / 2]);
              perim.push([ww / 2, -h / 2]);
              perim.push([ww / 2, h / 2 - hfl]);
              perim.push([wfl / 2, h / 2 - hfl]);
              perim.push([wfl / 2, h / 2]);
              perim.push([-wfl / 2, h / 2]);
              perim.push([-wfl / 2, h / 2 - hfl]);
              perim.push([-ww / 2, h / 2 - hfl]);
              perim.push([-ww / 2, -h / 2]);
            }

            break;
          case "VT":
            if (props.workingSec.params?.type === "vtParams") {
              let h = props.workingSec.params.vtParams.height;
              let hfl = props.workingSec.params.vtParams.flangeHeight;
              let wfl = props.workingSec.params.vtParams.flangeWidth;
              let ww_top = props.workingSec.params.vtParams.topWebWidth;
              let ww_btm = props.workingSec.params.vtParams.bottomWebWidth;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.vtParams.height = h;
              }
              if (k === "flangeHeight") {
                hfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.vtParams.flangeHeight = hfl;
              }
              if (k === "flangeWidth") {
                wfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.vtParams.flangeWidth = wfl;
              }
              if (k === "topWebWidth") {
                ww_top = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.vtParams.topWebWidth = ww_top;
              }
              if (k === "bottomWebWidth") {
                ww_btm = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.vtParams.bottomWebWidth = ww_btm;
              }

              if (
                Math.round(hfl * 1e3) > Math.round(h * 1e3) / 2 ||
                Math.round((wfl - ww_top) * 1e3) < 2 * minVal ||
                Math.round(ww_btm * 1e3) > Math.round(ww_top * 1e3)
              ) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              perim.push([-ww_btm / 2, -h / 2]);
              perim.push([ww_btm / 2, -h / 2]);
              perim.push([ww_top / 2, h / 2 - hfl]);
              perim.push([wfl / 2, h / 2 - hfl]);
              perim.push([wfl / 2, h / 2]);
              perim.push([-wfl / 2, h / 2]);
              perim.push([-wfl / 2, h / 2 - hfl]);
              perim.push([-ww_top / 2, h / 2 - hfl]);
              perim.push([-ww_btm / 2, -h / 2]);
            }
            break;
          case "VR":
            if (props.workingSec.params?.type === "vrParams") {
              let h = props.workingSec.params.vrParams.height;
              let w_btm = props.workingSec.params.vrParams.bottomWidth;
              let w_top = props.workingSec.params.vrParams.topWidth;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.vrParams.height = h;
              }
              if (k === "bottomWidth") {
                w_btm = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.vrParams.bottomWidth = w_btm;
              }
              if (k === "topWidth") {
                w_top = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.vrParams.topWidth = w_top;
              }

              perim.push([-w_btm / 2, -h / 2]);
              perim.push([w_btm / 2, -h / 2]);
              perim.push([w_top / 2, h / 2]);
              perim.push([-w_top / 2, h / 2]);
              perim.push([-w_btm / 2, -h / 2]);
            }
            break;
          case "F":
            if (props.workingSec.params?.type === "fParams") {
              let h = props.workingSec.params.fParams.height;
              let hfl = props.workingSec.params.fParams.flangeHeight;
              let wfl = props.workingSec.params.fParams.flangeWidth;
              let ww = props.workingSec.params.fParams.webWidth;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.fParams.height = h;
              }
              if (k === "flangeHeight") {
                hfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.fParams.flangeHeight = hfl;
              }
              if (k === "flangeWidth") {
                wfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.fParams.flangeWidth = wfl;
              }
              if (k === "webWidth") {
                ww = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.fParams.webWidth = ww;
              }

              if (Math.round(hfl * 1e3) > Math.round(h * 1e3) / 2 || Math.round((wfl - ww) * 1e3) < 2 * minVal) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              perim.push([-wfl / 2, -h / 2]);
              perim.push([wfl / 2, -h / 2]);
              perim.push([wfl / 2, -h / 2 + hfl]);
              perim.push([ww / 2, -h / 2 + hfl]);
              perim.push([ww / 2, h / 2]);
              perim.push([-ww / 2, h / 2]);
              perim.push([-ww / 2, -h / 2 + hfl]);
              perim.push([-wfl / 2, -h / 2 + hfl]);
              perim.push([-wfl / 2, -h / 2]);
            }
            break;
          case "L":
            if (props.workingSec.params?.type === "lParams") {
              let h = props.workingSec.params.lParams.height;
              let hfl = props.workingSec.params.lParams.flangeHeight;
              let wfl = props.workingSec.params.lParams.flangeWidth;
              let ww = props.workingSec.params.lParams.webWidth;
              let side = props.workingSec.params.lParams.side;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.lParams.height = h;
              }
              if (k === "flangeHeight") {
                hfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.lParams.flangeHeight = hfl;
              }
              if (k === "flangeWidth") {
                wfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.lParams.flangeWidth = wfl;
              }
              if (k === "webWidth") {
                ww = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.lParams.webWidth = ww;
              }
              if (k === "side") {
                side = Number(value);
                props.workingSec.params.lParams.side = side;
              }

              if (Math.round(hfl * 1e3) > Math.round(h * 1e3) / 2 || Math.round((wfl - ww) * 1e3) < minVal) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              let wsup = wfl - ww;

              let x = -wfl / 2;
              perim.push([x, -h / 2]);
              x += wfl;
              perim.push([x, -h / 2]);

              if (side === 2) {
                perim.push([x, -h / 2 + hfl]);
                x -= wsup;
                perim.push([x, -h / 2 + hfl]);
                perim.push([x, h / 2]);
              } else {
                perim.push([x, h / 2]);
              }

              x -= ww;
              perim.push([x, h / 2]);
              if (side === 1) {
                perim.push([x, -h / 2 + hfl]);
                x -= wsup;
                perim.push([x, -h / 2 + hfl]);
              }
              x = -wfl / 2;
              perim.push([x, -h / 2]);
            }
            break;
          case "KB":
            if (props.workingSec.params?.type === "kbParams") {
              let h = props.workingSec.params.kbParams.height;
              let w_btm = props.workingSec.params.kbParams.bottomWidth;
              let hfl_lft = props.workingSec.params.kbParams.leftFlangeHeight;
              let hfl_rgt = props.workingSec.params.kbParams.rightFlangeHeight;
              let wsup_lft = props.workingSec.params.kbParams.leftSupportWidth;
              let wsup_rgt = props.workingSec.params.kbParams.rightSupportWidth;
              let inc_lft = props.workingSec.params.kbParams.inclinationLeftFlange;
              let inc_rgt = props.workingSec.params.kbParams.inclinationRightFlange;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbParams.height = h;
              }
              if (k === "bottomWidth") {
                w_btm = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbParams.bottomWidth = w_btm;
              }
              if (k === "leftFlangeHeight") {
                hfl_lft = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbParams.leftFlangeHeight = hfl_lft;
              }
              if (k === "rightFlangeHeight") {
                hfl_rgt = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbParams.rightFlangeHeight = hfl_rgt;
              }
              if (k === "leftSupportWidth") {
                wsup_lft = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbParams.leftSupportWidth = wsup_lft;
              }
              if (k === "rightSupportWidth") {
                wsup_rgt = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbParams.rightSupportWidth = wsup_rgt;
              }
              if (k === "inclinationLeftFlange") {
                inc_lft = Number(value);
                props.workingSec.params.kbParams.inclinationLeftFlange = inc_lft;
              }
              if (k === "inclinationRightFlange") {
                inc_rgt = Number(value);
                props.workingSec.params.kbParams.inclinationRightFlange = inc_rgt;
              }

              let wmax_rgt = w_btm / 2 + hfl_rgt / inc_rgt;
              let wmax_lft = w_btm / 2 + hfl_lft / inc_lft;

              if (
                Math.round(Math.max(hfl_lft, hfl_lft) * 1e3) > (Math.round(h * 1e3) * 2) / 3 ||
                Math.round((wmax_rgt + wmax_lft - wsup_rgt - wsup_lft) * 1e3) < minVal ||
                inc_lft < 1 ||
                inc_rgt < 1 ||
                inc_lft > 10 ||
                inc_rgt > 10
              ) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              perim.push([-w_btm / 2, -h / 2]);
              perim.push([w_btm / 2, -h / 2]);
              perim.push([wmax_rgt, -h / 2 + hfl_rgt]);
              perim.push([wmax_rgt - wsup_rgt, -h / 2 + hfl_rgt]);
              perim.push([wmax_rgt - wsup_rgt, h / 2]);
              perim.push([-wmax_lft + wsup_lft, h / 2]);
              perim.push([-wmax_lft + wsup_lft, -h / 2 + hfl_lft]);
              perim.push([-wmax_lft, -h / 2 + hfl_lft]);
              perim.push([-w_btm / 2, -h / 2]);
            }
            break;
          case "KBE":
            if (props.workingSec.params?.type === "kbeParams") {
              let h = props.workingSec.params.kbeParams.height;
              let hfl = props.workingSec.params.kbeParams.flangeHeight;
              let wsup = props.workingSec.params.kbeParams.supportWidth;
              let ww = props.workingSec.params.kbeParams.webWidth;
              let w_btm = props.workingSec.params.kbeParams.bottomWidth;
              let side = props.workingSec.params.kbeParams.side;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbeParams.height = h;
              }
              if (k === "flangeHeight") {
                hfl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbeParams.flangeHeight = hfl;
              }
              if (k === "supportWidth") {
                wsup = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbeParams.supportWidth = wsup;
              }
              if (k === "webWidth") {
                ww = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbeParams.webWidth = ww;
              }
              if (k === "bottomWidth") {
                w_btm = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.kbeParams.bottomWidth = w_btm;
              }
              if (k === "side") {
                side = Number(value);
                props.workingSec.params.kbeParams.side = side;
              }

              if (
                Math.round(hfl * 1e3) > (Math.round(h * 1e3) * 2) / 3 ||
                Math.round((ww + wsup + hfl / 10) * 1e3) <= Math.round(w_btm * 1e3)
              ) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              let x = -w_btm / 2;
              perim.push([x, -h / 2]);
              x += w_btm;
              perim.push([x, -h / 2]);

              if (side === 2) {
                let a = ww + wsup - w_btm;
                x += a;
                perim.push([x, -h / 2 + hfl]);
                x -= wsup;
                perim.push([x, -h / 2 + hfl]);
                perim.push([x, h / 2]);
              } else {
                perim.push([x, h / 2]);
              }

              x -= ww;
              perim.push([x, h / 2]);
              if (side === 1) {
                perim.push([x, -h / 2 + hfl]);
                x -= wsup;
                perim.push([x, -h / 2 + hfl]);
              }
              x = -w_btm / 2;
              perim.push([x, -h / 2]);
            }
            break;
          case "I":
            if (props.workingSec.params?.type === "iParams") {
              let h = props.workingSec.params.iParams.height;
              let hfl_btm = props.workingSec.params.iParams.bottomFlangeHeight;
              let wfl_btm = props.workingSec.params.iParams.bottomFlangeWidth;
              let hw_btm = props.workingSec.params.iParams.bottomWebHeight;
              let hfl_top = props.workingSec.params.iParams.topFlangeHeight;
              let wfl_top = props.workingSec.params.iParams.topFlangeWidth;
              let hw_top = props.workingSec.params.iParams.topWebHeight;
              let ww = props.workingSec.params.iParams.webWidth;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.iParams.height = h;
              }
              if (k === "bottomFlangeHeight") {
                hfl_btm = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.iParams.bottomFlangeHeight = hfl_btm;
              }
              if (k === "bottomFlangeWidth") {
                wfl_btm = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.iParams.bottomFlangeWidth = wfl_btm;
              }
              if (k === "bottomWebHeight") {
                hw_btm = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.iParams.bottomWebHeight = hw_btm;
              }
              if (k === "topFlangeHeight") {
                hfl_top = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.iParams.topFlangeHeight = hfl_top;
              }
              if (k === "topFlangeWidth") {
                wfl_top = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.iParams.topFlangeWidth = wfl_top;
              }
              if (k === "topWebHeight") {
                hw_top = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.iParams.topWebHeight = hw_top;
              }
              if (k === "webWidth") {
                ww = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.iParams.webWidth = ww;
              }

              if (
                Math.round((hfl_btm + hfl_top + hw_btm + hw_top) * 1e3) > Math.round(h * 1e3) - minVal ||
                Math.round(wfl_btm * 1e3) < Math.round(ww * 1e3) + 2 * minVal ||
                Math.round(wfl_top * 1e3) < Math.round(ww * 1e3) + 2 * minVal
              ) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              let x = -wfl_btm / 2;
              if (wfl_btm < wfl_top) {
                x = -wfl_top / 2;
                x += (wfl_top - wfl_btm) / 2;
              }
              perim.push([x, -h / 2]);
              x += wfl_btm;
              perim.push([x, -h / 2]);
              perim.push([x, -h / 2 + hfl_btm]);
              x -= (wfl_btm - ww) / 2;
              perim.push([x, -h / 2 + hfl_btm + hw_btm]);
              perim.push([x, h / 2 - hfl_top - hw_top]);
              x += (wfl_top - ww) / 2;
              perim.push([x, h / 2 - hfl_top]);
              perim.push([x, h / 2]);
              x -= wfl_top;
              perim.push([x, h / 2]);
              perim.push([x, h / 2 - hfl_top]);
              x += (wfl_top - ww) / 2;
              perim.push([x, h / 2 - hfl_top - hw_top]);
              perim.push([x, -h / 2 + hfl_btm + hw_btm]);
              x -= (wfl_btm - ww) / 2;
              perim.push([x, -h / 2 + hfl_btm]);
              perim.push([x, -h / 2]);
            }
            break;

          //============ STEEL ===============
          case "HSQ":
            if (props.workingSec.params?.type === "hsqParams") {
              let h = props.workingSec.params.hsqParams.height;
              let w_top = props.workingSec.params.hsqParams.topWidth;
              let w = props.workingSec.params.hsqParams.width;
              let t_top_fl = props.workingSec.params.hsqParams.topFlangeThickness;
              let t_btm_fl = props.workingSec.params.hsqParams.bottomFlangeThickness;
              let t_web = props.workingSec.params.hsqParams.webThickness;
              let indent = props.workingSec.params.hsqParams.indent;
              let indent_cut = props.workingSec.params.hsqParams.indentCut;
              let cut = props.workingSec.params.hsqParams.cutOff;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.hsqParams.height = h;
              }
              if (k === "topWidth") {
                w_top = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.hsqParams.topWidth = w_top;
              }
              if (k === "width") {
                w = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.hsqParams.width = w;
              }
              if (k === "topFlangeThickness") {
                t_top_fl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.hsqParams.topFlangeThickness = t_top_fl;
              }
              if (k === "bottomFlangeThickness") {
                t_btm_fl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.hsqParams.bottomFlangeThickness = t_btm_fl;
              }
              if (k === "webThickness") {
                t_web = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.hsqParams.webThickness = t_web;
              }
              if (k === "indent") {
                indent = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.hsqParams.indent = indent;
              }
              if (k === "indentCut") {
                indent_cut = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.hsqParams.indentCut = indent_cut;
              }
              if (k === "cutOff") {
                cut = Number(value);
                props.workingSec.params.hsqParams.cutOff = cut;
              }

              let b_lft = indent;
              let b_rgt = indent;

              if (cut === 1 || cut === 3) {
                b_lft = indent_cut;
              }

              if (cut === 2 || cut === 3) {
                b_rgt = indent_cut;
              }

              if (
                (cut === 0 && Math.round((w_top + indent * 2 + t_web * 2) * 1e3) > Math.round(w * 1e3)) ||
                ((cut === 1 || cut === 2) &&
                  Math.round((w_top + indent + indent_cut + t_web * 2) * 1e3) > Math.round(w * 1e3)) ||
                (cut === 3 && Math.round((w_top + t_web * 2) * 1e3) > Math.round(w * 1e3))
              ) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              //================================================================================================

              // NOTE!!!
              // Identical code exist in NG-analysis-section repo in section.cpp file

              // derive x1's
              let tol = 1e-6;
              let h1 = h - t_btm_fl - t_top_fl / 2.0;

              const lamda = (dx: number, indent: number) => {
                let alfa = Math.atan(h1 / dx);

                let beta_low = alfa;
                let beta_high = Math.PI / 2.0;

                let beta = (beta_low + beta_high) / 2.0;

                let sol = h1 * Math.cos(beta) + t_web - dx * Math.sin(beta);

                while (Math.abs(sol) > tol) {
                  if (sol > 0) {
                    beta_low = beta;
                  } else {
                    beta_high = beta;
                  }

                  beta = (beta_low + beta_high) / 2.0;

                  sol = h1 * Math.cos(beta) + t_web - dx * Math.sin(beta);
                }

                let x1 = t_web / Math.sin(beta);

                return x1;
              };

              let x0_lft = 0;
              let x1_lft = t_web;

              let x0_rgt = 0;
              let x1_rgt = t_web;

              // x1 left --------------------------------

              if (cut === 0) {
                let dx = (w - w_top) / 2.0 - b_lft;

                x1_lft = lamda(dx, b_lft);
                x0_lft = dx - x1_lft;
              }

              if (cut === 2) {
                // how does left side behave when right side is cut off?
                let dx = w - (b_rgt + t_web + w_top) - b_lft;

                x1_lft = lamda(dx, b_lft);
                x0_lft = dx - x1_lft;
              }

              // x1 right --------------------------------

              if (cut === 0) {
                let dx = (w - w_top) / 2.0 - b_rgt;

                x1_rgt = lamda(dx, b_rgt);
                x0_rgt = dx - x1_rgt;
              }

              if (cut === 1) {
                // how does right side behave when left side is cut off?
                let dx = w - (b_lft + t_web + w_top) - b_rgt;

                x1_rgt = lamda(dx, b_rgt);
                x0_rgt = dx - x1_rgt;
              }

              //-------------------------------------------

              if (cut === 3) {
                b_lft = b_rgt = (w - w_top) / 2 - t_web;
              }

              // --------------- POLYLINE -----------------------------

              // lower flange
              let x = 0.0,
                y = 0.0;
              let xs = x,
                ys = y;

              perim.push([xs, ys]);
              x += w;
              perim.push([x, y]);
              y += t_btm_fl;
              perim.push([x, y]);
              x -= w;
              perim.push([x, y]);
              perim.push([xs, ys]);

              // top flange
              add = [];
              x = b_lft + x0_lft + x1_lft;
              y = h - t_top_fl;

              xs = x;
              ys = y;

              add.push([xs, ys]);
              x += w_top;
              add.push([x, y]);
              y += t_top_fl;
              add.push([x, y]);
              x -= w_top;
              add.push([x, y]);
              add.push([xs, ys]);

              adds.push(add);

              // left web
              add = [];
              x = b_lft;
              y = t_btm_fl;

              xs = x;
              ys = y;

              add.push([xs, ys]);
              x += x1_lft;
              add.push([x, y]);
              x += x0_lft;
              y += h1;
              add.push([x, y]);
              if (cut === 0 || cut === 2) {
                y += t_top_fl / 2.0;
                add.push([x, y]);
                y -= t_top_fl / 2.0;
              }
              x -= x1_lft;
              add.push([x, y]);
              add.push([xs, ys]);

              adds.push(add);

              // right web
              add = [];
              x = w - b_rgt - x1_rgt;
              y = t_btm_fl;

              xs = x;
              ys = y;

              add.push([xs, ys]);
              x += x1_rgt;
              add.push([x, y]);
              x -= x0_rgt;
              y += h1;
              add.push([x, y]);
              x -= x1_rgt;
              if (cut === 0 || cut === 1) {
                y += t_top_fl / 2.0;
                add.push([x, y]);
                y -= t_top_fl / 2.0;
              }
              add.push([x, y]);
              add.push([xs, ys]);

              adds.push(add);
            }
            break;
          case "UX":
            if (props.workingSec.params?.type === "uxParams") {
              let h = props.workingSec.params.uxParams.height;
              let w = props.workingSec.params.uxParams.width;
              let t_fl = props.workingSec.params.uxParams.flangeThickness;
              let t_web = props.workingSec.params.uxParams.webThickness;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.uxParams.height = h;
              }
              if (k === "width") {
                w = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.uxParams.width = w;
              }
              if (k === "flangeThickness") {
                t_fl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.uxParams.flangeThickness = t_fl;
              }
              if (k === "webThickness") {
                t_web = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.uxParams.webThickness = t_web;
              }

              if (Math.round(t_web * 1e3) > Math.round((w / 2.0) * 1e3)) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              // --------------- POLYLINE -----------------------------

              // lower flange
              let x = 0.0,
                y = 0.0;
              let xs = x,
                ys = y;

              perim.push([xs, ys]);
              x += w;
              perim.push([x, y]);
              y += t_fl;
              perim.push([x, y]);
              x -= w;
              perim.push([x, y]);
              perim.push([xs, ys]);

              // web
              add = [];
              x = 0;
              y = t_fl;

              xs = x;
              ys = y;

              add.push([xs, ys]);
              x += t_web;
              add.push([x, y]);
              y += h - 2 * t_fl;
              add.push([x, y]);
              x -= t_web;
              add.push([x, y]);
              add.push([xs, ys]);

              adds.push(add);

              // top flange
              add = [];
              x = 0;
              y = h - t_fl;

              xs = x;
              ys = y;

              add.push([xs, ys]);
              x += w;
              add.push([x, y]);
              y += t_fl;
              add.push([x, y]);
              x -= w;
              add.push([x, y]);
              add.push([xs, ys]);

              adds.push(add);
            }
            break;
          case "ZX":
            if (props.workingSec.params?.type === "zxParams") {
              let h = props.workingSec.params.zxParams.height;
              let w = props.workingSec.params.zxParams.width;
              let t_fl = props.workingSec.params.zxParams.flangeThickness;
              let t_web = props.workingSec.params.zxParams.webThickness;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.zxParams.height = h;
              }
              if (k === "width") {
                w = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.zxParams.width = w;
              }
              if (k === "flangeThickness") {
                t_fl = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.zxParams.flangeThickness = t_fl;
              }
              if (k === "webThickness") {
                t_web = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.zxParams.webThickness = t_web;
              }

              if (Math.round(t_web * 1e3) > Math.round((w / 2.0) * 1e3)) {
                ok = false;
                msg = "Invalid dimensions";
                break;
              }

              // --------------- POLYLINE -----------------------------

              // lower flange
              let x = w - t_web,
                y = 0.0;
              let xs = x,
                ys = y;

              perim.push([xs, ys]);
              x += w;
              perim.push([x, y]);
              y += t_fl;
              perim.push([x, y]);
              x -= w;
              perim.push([x, y]);
              perim.push([xs, ys]);

              // web
              add = [];
              x = w - t_web;
              y = t_fl;

              xs = x;
              ys = y;

              add.push([xs, ys]);
              x += t_web;
              add.push([x, y]);
              y += h - 2 * t_fl;
              add.push([x, y]);
              x -= t_web;
              add.push([x, y]);
              add.push([xs, ys]);

              adds.push(add);

              // top flange
              add = [];
              x = 0;
              y = h - t_fl;

              xs = x;
              ys = y;

              add.push([xs, ys]);
              x += w;
              add.push([x, y]);
              y += t_fl;
              add.push([x, y]);
              x -= w;
              add.push([x, y]);
              add.push([xs, ys]);

              adds.push(add);
            }
            break;

          //============ TIMBER ===============
          case "Sawn":
          case "Dressed":
          case "Glulam":
          case "Kerto":
            if (props.workingSec.params?.type === "rParams") {
              let h = props.workingSec.params.rParams.height;
              let w = props.workingSec.params.rParams.width;

              if (k === "height") {
                h = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.rParams.height = h;
              }
              if (k === "width") {
                w = Number(value) * 1e-3; // Convert to meters
                props.workingSec.params.rParams.width = w;
              }

              perim.push([-w / 2, -h / 2]);
              perim.push([w / 2, -h / 2]);
              perim.push([w / 2, h / 2]);
              perim.push([-w / 2, h / 2]);
              perim.push([-w / 2, -h / 2]);
            }
            break;

          default:
            break;
        }

        let pl: ng.geometry.IPoint2D[] = [];

        for (let i = 0; i < perim.length; i++) {
          let pnt: ng.geometry.Point2D = ng.geometry.Point2D.create({ x: perim[i][0], y: perim[i][1] });
          pl.push(pnt);
        }

        if (props.workingSec && props.workingSec.polylines && props.workingSec.polylines.perimeter && pl.length > 0) {
          props.workingSec.polylines.perimeter.points = pl;
        }

        let extra = [];
        for (let j = 0; j < adds.length; j++) {
          pl = [];
          for (let i = 0; i < adds[j].length; i++) {
            let pnt: ng.geometry.Point2D = ng.geometry.Point2D.create({ x: adds[j][i][0], y: adds[j][i][1] });
            pl.push(pnt);
          }

          if (props.workingSec && props.workingSec.polylines && props.workingSec.polylines.additionalPerimeters) {
            let add = ng.geometry.PolyLine2D.create({ points: pl });
            extra.push(add);
          }
        }

        if (props.workingSec && props.workingSec.polylines && props.workingSec.polylines.additionalPerimeters) {
          props.workingSec.polylines.additionalPerimeters = extra;
        }
      }

      if (ok) {
        setTmpDim(GetDim(props.workingSec));
        props.setSec(props.workingSec);
      }
    }

    if (!ok) {
      AlertMessage("info", title, subtitle, msg);
    }
  }

  //================================================================================================

  const { t } = useTranslation();
  return (
    <>
      <Container id="ngtable">
        <Row style={{ paddingTop: 10 }}>
          <div style={{ paddingLeft: "3.5em", paddingRight: "1em" }}>
            <Container>
              <Row className="justify-content-center align-items-center">
                <Col xs={1} className="text-center">
                  {t("name_.label")}
                </Col>
                <Col xs={2} className="text-center">
                  <SXEditField
                    fullWidth
                    textAlign="left"
                    type="text"
                    id={"labelname"}
                    disableNegativeNumber={true}
                    onUpdate={(value: string) => {
                      // LabelUpdate(value);
                      funUpdate(value, "changeLabel");
                    }}
                    val={props!.workingSec!.id!.name}
                  />
                </Col>
                <Col xs={9}></Col>
              </Row>
            </Container>
          </div>
        </Row>
      </Container>
      {settings && (
        <Container id="ngtable">
          <Row style={{ paddingTop: 10 }}>
            <div style={{ paddingLeft: "2em", paddingRight: "1em" }}>
              <Container>
                <Row>
                  <Col xs={3}>
                    {tmpDim &&
                      Object.keys(tmpDim).map((k, i) => {
                        if (
                          k === "radius" ||
                          i === 0 || // Skip the first key or the first
                          (type === "PL" && i === 2) || // Skip the first key and the last key (=width) if plate.
                          k === "cutOff" || // restrict HSQ cutOff to vertical for now.
                          k === "indentCut" || //see above
                          k === "indent" // see above
                        ) {
                          if (k === "cutOff" && type === "HSQ") {
                            radioBtn = 3;
                          }

                          return null;
                        }

                        let unitVal = GetUnitVal(tmpDim, k);

                        if (type === "KB" && (k === "inclinationLeftFlange" || k === "inclinationRightFlange")) {
                          unitVal = Number(unitVal) * 1e-3;
                        }

                        if (k === "side" || k === "cutOff") {
                          radioBtn = Number(unitVal);
                        }

                        let disabled = false;
                        if (
                          ((GetUnitVal(tmpDim, "cutOff") === 0 || GetUnitVal(tmpDim, "cutOff") === 3) &&
                            k === "indentCut") ||
                          (GetUnitVal(tmpDim, "cutOff") === 3 && k === "indent")
                        ) {
                          disabled = true;
                        }

                        return (
                          <Row
                            className="table-header align-items-center"
                            key={i}
                            id={i.toString()}
                            onMouseEnter={MouseEnter}
                            onMouseLeave={MouseLeave}
                            style={{ marginTop: "0.5em" }}
                          >
                            <>
                              <Col className="text-center table-header-bg">{t(`${k}_.label`)}</Col>
                              <Col className="text-center">
                                {k !== "cutOff" && (
                                  <SXEditField
                                    disabled={disabled}
                                    fullWidth
                                    textAlign="left"
                                    type="number"
                                    id={"size_" + i}
                                    disableNegativeNumber={true}
                                    onUpdate={(value: number) => {
                                      funUpdate(value, k);
                                    }}
                                    val={Number(unitVal) * 1e3}
                                  />
                                )}
                                {false && k === "side" && (
                                  <>
                                    <Row>
                                      <Form.Check
                                        className="ngradiobutton pointer"
                                        inline
                                        label={"Left"}
                                        name={"lft"}
                                        type={"radio"}
                                        id={"lft"}
                                        checked={Number(unitVal) === 1 ? true : false}
                                        onChange={() => {
                                          funUpdate(1, k);
                                        }}
                                      />
                                    </Row>
                                    <Row>
                                      <Form.Check
                                        className="ngradiobutton pointer"
                                        inline
                                        label={"Right"}
                                        name={"rgt"}
                                        type={"radio"}
                                        id={"rgt"}
                                        checked={Number(unitVal) === 2 ? true : false}
                                        onChange={() => {
                                          funUpdate(2, k);
                                        }}
                                      />
                                    </Row>
                                  </>
                                )}
                                {k === "cutOff" && (
                                  <>
                                    <Row>
                                      <Dropdown
                                        id="cutoff-dropdown"
                                        onSelect={(e) => {
                                          funUpdate(Number(e), k);
                                        }}
                                      >
                                        <Dropdown.Menu>
                                          <Dropdown.Item key={0} eventKey={0}>
                                            {t("none_.label")}
                                          </Dropdown.Item>
                                          <Dropdown.Item key={1} eventKey={1}>
                                            {t("left_.label")}
                                          </Dropdown.Item>
                                          <Dropdown.Item key={2} eventKey={2}>
                                            {t("right_.label")}
                                          </Dropdown.Item>
                                          <Dropdown.Item key={3} eventKey={3}>
                                            {t("both_.label")}
                                          </Dropdown.Item>
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    </Row>
                                  </>
                                )}
                              </Col>
                            </>
                          </Row>
                        );
                      })}
                  </Col>
                  <Col xs={9} className="gridbg border p-1">
                    {props.workingSec && props.workingSec.polylines && props.workingSec.polylines.perimeter && (
                      <SectionSVG
                        section={ng.sections.Section.toObject(props.workingSec) ?? {}}
                        showAxes={false}
                        zoomFactor={1.2}
                        pad={0.1}
                        hlpNbr={hlpNbr}
                        grid={true}
                      />
                    )}
                  </Col>
                </Row>
              </Container>
            </div>
          </Row>
        </Container>
      )}
    </>
  );
};
